import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Delete } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker, LoadingButton } from '@mui/lab';
import {
  TextField,
  Button,
  Typography,
  Autocomplete,
  Box,
  LinearProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { AxiosError } from 'axios';

import { submitValidatedWorkClaim } from 'api';
import { AlertModal } from 'components/AlertModal';
import { SelectRow } from 'components/SelectRow';
import {
  parsedContractID,
  setDateErrors,
  validateEmail,
  reasonForSeparationChoicesObject,
  forCauseChoicesObject,
  resignedChoicesObject,
  employmentOptions,
  countryOptions,
  getErrorDetail,
} from 'helpers';
import { useCandidateContext } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import {
  ValidatedWorkClaimFormProps,
  ValidatedWorkPeriodFormErrors,
} from 'types';

import { Details } from '../Details';
import {
  FieldGroupWrapper,
  FieldGroupBorder,
  ContentWrapper,
} from './styledComponents';

const setDateErrorText = (errorState: ValidatedWorkPeriodFormErrors) => {
  if (errorState?.dateOrderError) {
    return 'End date must be after start date';
  }
  if (errorState?.dateDiffError) {
    return 'There are 100 years between the start and end date you entered. That seems unrealistic.';
  }

  if (errorState?.dateOrderError) {
    return 'End date must be after start date';
  }
  return '';
};

export const ValidatedWorkPeriod: React.FC<ValidatedWorkClaimFormProps> = ({
  contract_id,
  cage_number,
  badge_id,
  job_location,
  job_title,
  employer_provided_date_of_birth,
  full_part_time,
  reason_for_separation,
  employment_details,
  employment_details_other,
  contract_supervisor_given_name,
  contract_supervisor_surname,
  contract_supervisor_email,
  contract_additional_information,
  contract_complete,
  validated_work_claim_start_date,
  validated_work_claim_end_date,
  ready_for_admin_approval,
  candidate_id,
  organization_id,
  review_status,
  id,
  open,
  index,
  subcontract_details,
  setCandidate,
  handleWorkPeriodRemove,
  setClaimErrors,
  ...rest
}) => {
  const [mounted, setMounted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contractId, setContractId] = useState(contract_id);
  const [badgeId, setBadgeId] = useState(badge_id);
  const [jobLocation, setJobLocation] = useState(job_location);
  const [fullPartTime, setFullPartTime] = useState(full_part_time);
  const [cageId, setCageId] = useState(cage_number);
  const [supervisorGivenName, setSupervisorGivenName] = useState(
    contract_supervisor_given_name,
  );
  const [jobTitle, setJobTitle] = useState(job_title);
  const [birthdate, setBirthdate] = useState(employer_provided_date_of_birth);
  const [supervisorSurname, setSupervisorSurname] = useState(
    contract_supervisor_surname,
  );
  const [supervisorEmail, setSupervisorEmail] = useState(
    contract_supervisor_email,
  );
  const [reasonForSeparation, setReasonForSeparation] = useState(
    reason_for_separation,
  );
  const [employmentDetails, setEmploymentDetails] =
    useState(employment_details);
  const [employmentDetailsOther, setEmploymentDetailsOther] = useState(
    employment_details_other,
  );
  const [workClaimAdditionalInformation, setWorkClaimAdditionalInformation] =
    useState(contract_additional_information);

  const [cageNcageInfo, setCageNcageInfo] = useState(false);

  const [subContractId, setSubContractId] = useState(subcontract_details);
  const [startDate, setStartDate] = useState(validated_work_claim_start_date);
  const [endDate, setEndDate] = useState(validated_work_claim_end_date);
  const [openDetails, setOpenDetails] = useState(open);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [errorState, setErrorState] = useState({
    dateCompleteError: false,
    dateOrderError: false,
    dateDiffError: false,
    reasonForSeparationError: false,
    contractNumberFormatError: false,
    confirmingError: false,
    countryError: false,
    startDateFormatError: false,
    endDateFormatError: false,
    supervisorEmailError: false,
  });
  const { updateCandidate } = useCandidateContext();
  const { setSnack } = useSnackContext();

  const disabled = useMemo(() => {
    const isDisabled = Object.values(errorState).some(
      (error) => error === true,
    );
    return isDisabled;
  }, [errorState]);
  useEffect(() => {
    setMounted(true);
    setClaimErrors((prevErrors) => [
      ...prevErrors.slice(0, index),
      disabled,
      ...prevErrors.slice(index + 1),
    ]);
    return () => setMounted(false);
  }, [disabled, index, setClaimErrors]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        if (!startDate || !endDate || !reasonForSeparation) {
          return setErrorState((prevErrors) => ({
            ...prevErrors,
            dateDiffError: !startDate || !endDate,
            reasonForSeparationError: !reasonForSeparation,
          }));
        }

        const data = {
          contract_id: contractId ?? '',
          subcontract_details: subContractId ?? '',
          job_title: jobTitle ?? '',
          employer_provided_date_of_birth: birthdate,
          badge_id: badgeId ?? '',
          cage_number: cageId ?? '',
          job_location: jobLocation ?? '',
          full_part_time: fullPartTime ?? null,
          reason_for_separation: reasonForSeparation ?? '',
          employment_details: employmentDetails ?? '',
          employment_details_other: employmentDetailsOther ?? '',
          contract_supervisor_given_name: supervisorGivenName ?? '',
          contract_supervisor_surname: supervisorSurname ?? '',
          contract_supervisor_email: supervisorEmail ?? '',
          contract_additional_information: workClaimAdditionalInformation ?? '',
          validated_work_claim_start_date: startDate,
          validated_work_claim_end_date: endDate,
          contract_complete: true,
          ready_for_admin_approval: null,
          candidate_id: candidate_id,
          organization_id: null,
          id: id,
        };
        setSubmitting(true);
        const { data: validated_work_claim } = await submitValidatedWorkClaim(
          data,
        );
        setCandidate((prevCandidate) => ({
          ...prevCandidate,
          review_status: 'review_in_progress',
          validated_work_claims: [
            ...prevCandidate.validated_work_claims.slice(0, index),
            {
              ...prevCandidate.validated_work_claims[index],
              ...validated_work_claim,
            },
            ...prevCandidate.validated_work_claims.slice(index + 1),
          ],
        }));
        updateCandidate({
          id: candidate_id,
          review_status: 'review_in_progress',
        });
        setSnack((props) => ({
          ...props,
          severity: 'success',
          open: true,
          message: 'Work Period Saved!',
        }));
      } catch (e) {
        if ((e as AxiosError).response?.status === 403) {
          setSnack((props) => ({
            ...props,
            severity: 'warning',
            open: true,
            message: 'Cannot Modify A Submitted Work Claim',
          }));
        } else {
          setSnack((props) => ({
            ...props,
            severity: 'error',
            open: true,
            message: getErrorDetail({
              error: e,
              fallback: 'Failed To Save Work Period',
            }),
            error: e as Error,
          }));
        }
      } finally {
        // NOTE: component can suddenly unmount which react conciders a memory leak
        mounted && setSubmitting(false);
      }
    },
    [
      badgeId,
      jobTitle,
      cageId,
      candidate_id,
      contractId,
      birthdate,
      employmentDetails,
      employmentDetailsOther,
      supervisorEmail,
      supervisorGivenName,
      supervisorSurname,
      endDate,
      fullPartTime,
      id,
      index,
      jobLocation,
      mounted,
      reasonForSeparation,
      setCandidate,
      setSnack,
      startDate,
      subContractId,
      updateCandidate,
      workClaimAdditionalInformation,
    ],
  );

  return (
    <Details
      saved={Boolean(id)}
      error={
        errorState
          ? Object.values(errorState).some((error) => error === true)
          : false
      }
      headingText={`Work Period: ${contract_id || index + 1}`}
      {...rest}
      expanded={openDetails ? openDetails : false}
      onClick={(e) => {
        e.preventDefault();
        setOpenDetails(!openDetails);
      }}
      actionButton={
        <IconButton
          onClick={() => setDeleteAlert(true)}
          disabled={review_status === 'reviewed_and_verified'}
        >
          <Delete fontSize='large' />
        </IconButton>
      }
    >
      <ContentWrapper>
        <FieldGroupBorder>
          {submitting && <LinearProgress />}
          <FieldGroupWrapper>
            <Typography variant='h5' sx={{ mb: 2 }}>
              In what country was this work performed?
            </Typography>
            <Autocomplete
              id='country-select'
              options={countryOptions}
              isOptionEqualToValue={(option, value) => {
                return option.value === value.value;
              }}
              value={
                countryOptions.find(
                  (countryOption) => countryOption.value === jobLocation,
                ) || { label: 'Afghanistan', value: 'AF' }
              }
              autoHighlight
              onChange={(_, option) => {
                option && setJobLocation(option.value);
              }}
              getOptionLabel={(option) => option.label}
              renderOption={(props, option) => (
                <Box
                  component='li'
                  sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  <img
                    loading='lazy'
                    width='20'
                    src={`https://flagcdn.com/w20/${option.value.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${option.value.toLowerCase()}.png 2x`}
                    alt=''
                  />
                  {option.label} ({option.value})
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Choose a country'
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'select country', // disable autocomplete and autofill
                  }}
                />
              )}
            />
          </FieldGroupWrapper>
        </FieldGroupBorder>
        <FieldGroupBorder>
          <FieldGroupWrapper>
            <Typography variant='h5'>Contract Details</Typography>
            <TextField
              label='Supervisor Given Name'
              placeholder=''
              required={false}
              value={supervisorGivenName || ''}
              onChange={({ target: { value } }) =>
                setSupervisorGivenName(value)
              }
            />
            <TextField
              label='Supervisor Surname'
              placeholder=''
              required={false}
              value={supervisorSurname || ''}
              onChange={({ target: { value } }) => setSupervisorSurname(value)}
            />
            <TextField
              label='Supervisor Email'
              placeholder=''
              required={false}
              value={supervisorEmail || ''}
              onChange={({ target: { value } }) => {
                setErrorState((prevError) => {
                  return { ...prevError, supervisorEmailError: false };
                });
                setSupervisorEmail(value);
                setErrorState((prevErrors) => ({
                  ...prevErrors,
                  supervisorEmailError: !validateEmail(value),
                }));
              }}
              helperText={
                errorState?.supervisorEmailError
                  ? 'Email is incorrectly formatted'
                  : ''
              }
              error={errorState?.supervisorEmailError}
            />
            <TextField
              label='Prime Contract Number'
              placeholder='Example: F33615-16-P0002'
              required={false}
              value={contractId || ''}
              onChange={({ target: { value } }) => {
                setContractId(value);
                setErrorState((prevErrors) => ({
                  ...prevErrors,
                  contractNumberFormatError:
                    parsedContractID(value).length !== 13,
                }));
              }}
              helperText={
                errorState?.contractNumberFormatError
                  ? `Contract ID must be 13 characters long, you've entered ${
                      contractId && parsedContractID(contractId).length
                    }`
                  : ''
              }
              error={errorState?.contractNumberFormatError}
            />
            <TextField
              label='Subcontract Number (If Applicable)'
              placeholder='Example: F33615-16-P0002'
              required={false}
              value={subContractId || ''}
              onChange={({ target: { value } }) => setSubContractId(value)}
            />
            <TextField
              className='cage_ncage_text'
              label='CAGE or NCAGE Number'
              placeholder='Example: 7AUQ6'
              required={false}
              value={cageId || ''}
              onChange={({ target: { value } }) => setCageId(value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setCageNcageInfo((prev) => !prev)}
                    >
                      <InfoOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {cageNcageInfo && (
              <section className='cage_ncage_info'>
                <div className='cage'>
                  <b>CAGE: </b>
                  <span>Commercial And Government Entity</span>
                </div>
                <div className='ncage'>
                  <b>NCAGE: </b>
                  <span>NATO Commercial And Government Entity</span>
                </div>
              </section>
            )}
          </FieldGroupWrapper>
        </FieldGroupBorder>
        <FieldGroupBorder>
          <FieldGroupWrapper>
            <Typography gutterBottom variant='h5'>
              Employee Details
            </Typography>
            <TextField
              className='job_title'
              label='Job Title'
              placeholder='Example: Driver'
              required={false}
              value={jobTitle || ''}
              onChange={({ target: { value } }) => setJobTitle(value)}
            />
            <DatePicker
              label='Date Of Birth'
              mask='__/__/____'
              inputFormat='dd/MM/yyyy'
              maxDate={new Date()}
              onChange={(date) => {
                if (date) {
                  setBirthdate(date);
                }
              }}
              value={birthdate}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label='Work Period Start Date'
              mask='__/__/____'
              inputFormat='dd/MM/yyyy'
              maxDate={new Date()}
              onChange={useCallback(
                (date: Date | null) => {
                  setStartDate(date);
                  setErrorState((prevErrors) => {
                    return {
                      ...prevErrors,
                      ...setDateErrors(date, endDate),
                    };
                  });
                },
                [endDate],
              )}
              value={startDate ?? startDate}
              renderInput={useCallback(
                (params) => (
                  <TextField
                    {...params}
                    error={
                      errorState?.startDateFormatError ||
                      errorState?.dateCompleteError ||
                      errorState?.dateOrderError ||
                      errorState?.dateDiffError
                    }
                    helperText={
                      errorState?.startDateFormatError
                        ? 'date format must be: DD/MM/YYYY'
                        : setDateErrorText(errorState)
                    }
                  />
                ),
                [errorState],
              )}
            />
            <DatePicker
              label='Work Period End Date'
              mask='__/__/____'
              inputFormat='dd/MM/yyyy'
              maxDate={new Date()}
              onChange={useCallback(
                (date: Date | null) => {
                  setEndDate(date);
                  setErrorState((prevErrors) => {
                    return {
                      ...prevErrors,
                      ...setDateErrors(startDate, date),
                    };
                  });
                },
                [startDate],
              )}
              value={endDate ?? endDate}
              renderInput={useCallback(
                (params) => (
                  <TextField
                    {...params}
                    error={
                      errorState?.endDateFormatError ||
                      errorState?.dateCompleteError ||
                      errorState?.dateOrderError ||
                      errorState?.dateDiffError
                    }
                    helperText={
                      errorState?.endDateFormatError
                        ? 'date format must be: DD/MM/YYYY'
                        : setDateErrorText(errorState)
                    }
                  />
                ),
                [errorState],
              )}
            />
            <TextField
              label='Employee Badge ID'
              required={false}
              value={badgeId || ''}
              onChange={({ target: { value } }) => setBadgeId(value)}
            />
            <SelectRow
              id='full_part_time'
              title='Full or Part Time'
              alert
              required={false}
              defaultValue={fullPartTime || ''}
              value={
                employmentOptions.find(
                  (employmentOption) => employmentOption.value === fullPartTime,
                )?.value || ''
              }
              onChange={({ target: { value } }) =>
                setFullPartTime((value as string) || '')
              }
              options={employmentOptions}
            />
          </FieldGroupWrapper>
        </FieldGroupBorder>
        <FieldGroupBorder>
          <FieldGroupWrapper>
            <Typography gutterBottom variant='h5'>
              End of Employment
            </Typography>
            <SelectRow
              id='reason_for_separation'
              title='Reason For Separation'
              alert
              defaultValue={reason_for_separation || ''}
              required
              value={
                reasonForSeparationChoicesObject.find(
                  (reasonForSeparationOption) =>
                    reasonForSeparationOption.value === reasonForSeparation,
                )?.value || ''
              }
              onChange={({ target: { value } }) => {
                setReasonForSeparation((value as string) || '');
                errorState.reasonForSeparationError &&
                  setErrorState((prevErrors) => {
                    return {
                      ...prevErrors,
                      reasonForSeparationError: false,
                    };
                  });
                if (value !== 'resigned' && value !== 'for_cause') {
                  setEmploymentDetails('');
                  setEmploymentDetailsOther('');
                }
              }}
              options={reasonForSeparationChoicesObject}
              error={errorState?.reasonForSeparationError}
              helperText={
                errorState?.reasonForSeparationError
                  ? 'Please select a reason for separation.'
                  : ''
              }
            />
            {reasonForSeparation === 'for_cause' && (
              <SelectRow
                id='employment_details_for_cause'
                title='Employment Details: For Cause'
                alert
                defaultValue={employmentDetails || ''}
                required
                value={
                  forCauseChoicesObject.find(
                    (reason) => reason.value === employmentDetails,
                  )?.value || ''
                }
                onChange={(e) => {
                  const { value } = e.target;
                  if (value !== 'other') {
                    setEmploymentDetailsOther('');
                  }
                  setEmploymentDetails((value as string) || '');
                }}
                options={forCauseChoicesObject}
              />
            )}
            {reasonForSeparation === 'resigned' && (
              <SelectRow
                id='employment_details_resigned'
                title='Employment Details: Resigned'
                alert
                defaultValue={employmentDetails || ''}
                required
                value={
                  resignedChoicesObject.find(
                    (reason) => reason.value === employmentDetails,
                  )?.value || ''
                }
                onChange={({ target: { value } }) =>
                  setEmploymentDetails((value as string) || '')
                }
                options={resignedChoicesObject}
              />
            )}
            {employmentDetails === 'other' && (
              <TextField
                label='Employment Details: Other'
                required={false}
                rows={8}
                maxRows={12}
                value={employmentDetailsOther || ''}
                onChange={({ target: { value } }) =>
                  setEmploymentDetailsOther(value || '')
                }
              />
            )}
          </FieldGroupWrapper>
        </FieldGroupBorder>
        <FieldGroupBorder>
          <FieldGroupWrapper>
            <Typography variant='h5'>Additional Information</Typography>
            <p>
              If you have any relevant additional information about this period
              of performance, please enter it below.
            </p>
            <TextField
              required={false}
              value={workClaimAdditionalInformation || ''}
              onChange={({ target: { value } }) =>
                setWorkClaimAdditionalInformation(value || '')
              }
              multiline
              rows={8}
            />
          </FieldGroupWrapper>
        </FieldGroupBorder>
        <FieldGroupBorder>
          <FieldGroupWrapper>
            <Typography variant='h5'>Confirming Information</Typography>
            <p>
              Have you submitted all the information you have on this work
              period?
            </p>
            <LoadingButton
              sx={{
                marginBottom: '24px',
              }}
              variant='contained'
              type='submit'
              disabled={disabled}
              loading={submitting}
              onClick={onSubmit}
            >
              SAVE WORK PERIOD
            </LoadingButton>
          </FieldGroupWrapper>
          <Button
            variant='text'
            color='inherit'
            onClick={() => setDeleteAlert(true)}
            startIcon={<Delete />}
          >
            DELETE WORK PERIOD
          </Button>
        </FieldGroupBorder>
      </ContentWrapper>
      <AlertModal
        open={deleteAlert}
        heading='Are you sure you want to delete this Work Period?'
        subHeading="This can't be undone."
      >
        <Button variant='text' onClick={() => setDeleteAlert(false)}>
          CANCEL
        </Button>
        <Button
          variant='contained'
          onClick={() => {
            handleWorkPeriodRemove({
              index: index,
              validated_work_claim_id: id,
            });
            setDeleteAlert(false);
          }}
        >
          DELETE
        </Button>
      </AlertModal>
    </Details>
  );
};
